// Error / crash reporting — Sentry, wrapped so the rest of Guard Web can't misuse it.
//
// Same posture as PostHog analytics (./analytics.ts) and the console wrapper:
//   - SDK loads lazily and ONLY when a runtime-injected browser DSN exists. No DSN = frozen no-op.
//   - sendDefaultPii is off; identify is opaque user UUID + last-4 account id only.
//   - Session replay and performance traces stay OFF (field maps / capture flows).
//   - Ready for a later human "report a problem" flow via captureUserReport + event ids.

export interface SentryLike {
  init(options: Record<string, unknown>): void;
  setUser(user: { id: string } | null): void;
  setTag(key: string, value: string): void;
  captureException(error: unknown, hint?: Record<string, unknown>): string;
  captureMessage(message: string, level?: string): string;
  lastEventId(): string | undefined;
  captureFeedback?(
    feedback: { message: string; associatedEventId?: string },
    opts?: Record<string, unknown>,
  ): string;
}

export interface ErrorReporter {
  readonly enabled: boolean;
  readonly ready: Promise<void>;
  identify(userId: string, accountTail: string): void;
  clearUser(): void;
  captureException(
    error: unknown,
    context?: { tags?: Record<string, string>; fingerprint?: string[] },
  ): string | undefined;
  captureMessage(message: string, level?: "info" | "warning" | "error"): string | undefined;
  lastEventId(): string | undefined;
  captureUserReport(options: { message: string; associatedEventId?: string }): string | undefined;
}

export const NOOP_ERROR_REPORTER: ErrorReporter = Object.freeze({
  enabled: false,
  ready: Promise.resolve(),
  identify() {},
  clearUser() {},
  captureException() {
    return undefined;
  },
  captureMessage() {
    return undefined;
  },
  lastEventId() {
    return undefined;
  },
  captureUserReport() {
    return undefined;
  },
});

async function defaultLoader(): Promise<SentryLike> {
  const mod = await import("@sentry/react");
  return mod as unknown as SentryLike;
}

const MAX_REPORT_CHARS = 4000;

export function createErrorReporter(options: {
  dsn: string | null | undefined;
  environment: string | null | undefined;
  surface: "console" | "guard-web";
  release: string;
  loader?: () => Promise<SentryLike>;
}): ErrorReporter {
  const { dsn, environment, surface, release } = options;
  if (dsn === undefined || dsn === null) {
    return NOOP_ERROR_REPORTER;
  }
  const loader = options.loader ?? defaultLoader;

  let client: SentryLike | null = null;
  let loadStarted = false;
  const pending: Array<(sentry: SentryLike) => void> = [];
  let resolveReady!: () => void;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  function run(action: (sentry: SentryLike) => void): void {
    if (client !== null) {
      action(client);
      return;
    }
    pending.push(action);
    if (loadStarted) {
      return;
    }
    loadStarted = true;
    void loader()
      .then((sentry) => {
        sentry.init({
          dsn,
          environment: environment ?? "production",
          release,
          sendDefaultPii: false,
          tracesSampleRate: 0,
          integrations: [],
          beforeSend(event: {
            request?: { cookies?: unknown; headers?: Record<string, string> };
            user?: { email?: string; ip_address?: string; username?: string };
          }) {
            if (event.request) {
              delete event.request.cookies;
              if (event.request.headers) {
                for (const key of Object.keys(event.request.headers)) {
                  if (/auth|cookie|token|apikey|authorization/i.test(key)) {
                    delete event.request.headers[key];
                  }
                }
              }
            }
            if (event.user) {
              delete event.user.email;
              delete event.user.ip_address;
              delete event.user.username;
            }
            return event;
          },
        });
        sentry.setTag("surface", surface);
        client = sentry;
        for (const action of pending.splice(0)) {
          action(sentry);
        }
        resolveReady();
      })
      .catch(() => {
        pending.length = 0;
        resolveReady();
      });
  }

  return {
    enabled: true,
    ready,
    identify(userId, accountTail) {
      run((sentry) => {
        sentry.setUser({ id: userId });
        sentry.setTag("account_tail", accountTail);
      });
    },
    clearUser() {
      run((sentry) => {
        sentry.setUser(null);
      });
    },
    captureException(error, context) {
      let eventId: string | undefined;
      run((sentry) => {
        eventId = sentry.captureException(error, {
          tags: context?.tags,
          fingerprint: context?.fingerprint,
        });
      });
      return eventId;
    },
    captureMessage(message, level = "info") {
      let eventId: string | undefined;
      run((sentry) => {
        eventId = sentry.captureMessage(message, level);
      });
      return eventId;
    },
    lastEventId() {
      return client?.lastEventId() ?? undefined;
    },
    captureUserReport({ message, associatedEventId }) {
      const trimmed = message.trim().slice(0, MAX_REPORT_CHARS);
      if (trimmed.length === 0) {
        return undefined;
      }
      let eventId: string | undefined;
      run((sentry) => {
        if (typeof sentry.captureFeedback === "function") {
          const feedback: { message: string; associatedEventId?: string } = { message: trimmed };
          if (associatedEventId !== undefined) {
            feedback.associatedEventId = associatedEventId;
          }
          eventId = sentry.captureFeedback(feedback);
          return;
        }
        eventId = sentry.captureMessage(trimmed, "info");
        sentry.setTag("user_report", "true");
        if (associatedEventId !== undefined) {
          sentry.setTag("associated_event_id", associatedEventId);
        }
      });
      return eventId;
    },
  };
}
